import {
	ResearchGroup,
	Cross,
	LocalizationTypes,
	Question,
	Feasibility,
	UserCross,
	CrossSwitches,
	ProfileChosen,
} from '@/types/research';
import { QuestionnaireStatus } from '@/api/PanelistApi';
import { GusRegionStats, Region } from '@/api/RegionsApi';
import { Gender, StringSignature } from '@/types/types';
import { flatten, isEqual, omit, pick } from 'lodash';
import {
	AgeSpanWithoutValue,
	CrossWithoutAmount,
} from '@/pages/authenticated/statistics/FeasibilityStatistics';
import GUSData from '@/data/GUSData';
import hash from 'object-hash';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export const AGE_SPANS = [
	[APP_CONFIG.MIN_AGE, 24],
	[25, 34],
	[35, 44],
	[45, 54],
	[55, 64],
	[65, APP_CONFIG.MAX_AGE],
];

export function generateAgeSpans(
	span: [number, number],
	quantity: number
): { id: string; span: [number, number]; value: number }[] {
	const result: { id: string; span: [number, number]; value: number }[] = [];

	const first = AGE_SPANS.find((s) => span[0] >= s[0] && span[0] <= s[1]);

	if (!first) return result;

	result.push({
		id: crypto.randomUUID(),
		span: [span[0], Math.min(first[1], span[1])],
		value: 0,
	});

	for (const sp of AGE_SPANS) {
		if (sp[1] < span[1] && sp[0] > first[1])
			result.push({ id: crypto.randomUUID(), span: [sp[0], sp[1]], value: 0 });
	}

	const last = AGE_SPANS.find((s) => s[1] >= span[1]);

	if (last && last[1] !== first[1])
		result.push({
			id: crypto.randomUUID(),
			span: [last[0], span[1]],
			value: 0,
		});

	return result.map((sp, idx) => ({
		...sp,
		value:
			Math.floor(quantity / result.length) +
			(idx < quantity % result.length ? 1 : 0),
	}));
}

export function generateCrossing(group: ResearchGroup) {
	const { crossSwitches, userCrosses, questionsChosen, quantity } = group;

	const markedSwitches = countCrossSwitches(crossSwitches);

	let crosses: Cross[] =
		markedSwitches > 1
			? userCrosses.map((cross) => omit(cross, 'feasibility'))
			: [
					{
						gender: Gender.BOTH,
						ageFrom: APP_CONFIG.MIN_AGE,
						ageTo: APP_CONFIG.MAX_AGE,
						answers: [],
						province: null,
						county: null,
						city: null,
						postalCode: null,
						amount: group.amountOfQuestionnaires,
					},
			  ];

	if (markedSwitches !== questionsChosen.length + 3) {
		const shouldCrossGenders =
				!crossSwitches.gender &&
				(!quantity.gender.isAuto || group.gender !== Gender.BOTH),
			shouldCrossRegions =
				!crossSwitches.region &&
				(!quantity.region.isAuto ||
					group.regionSwitch !== LocalizationTypes.COUNTRY),
			shouldCrossAgeSpans = !crossSwitches.age && !quantity.age.isAuto;

		if (shouldCrossGenders) {
			crosses = generateGenderCrosses(crosses, group);
		}

		if (shouldCrossRegions) {
			crosses = generateRegionCrosses(crosses, group);
		}

		if (shouldCrossAgeSpans) {
			crosses = generateAgeCrosses(crosses, group);
		}

		const crossedQuestionIds = Object.keys(
			omit(crossSwitches, ['age', 'gender'])
		).filter((key) => crossSwitches[key]);

		const unCrossedQuestions = questionsChosen.filter(
			(qc) => !crossedQuestionIds.includes(qc.id)
		);

		for (const question of unCrossedQuestions) {
			crosses = generateQuestionCrosses(question, crosses, group);
		}
	}

	for (const cross of crosses) {
		const id = hash(
			{
				...omit(cross, ['id', 'amount', 'feasibility']),
				answers: cross.answers.map((a) => a.id),
			},
			{
				algorithm: 'md5',
			}
		);

		cross.id = id;
	}

	return crosses;
}

export function generateCrossingFromData(
	group: ResearchGroup,
	data: GusRegionStats,
	skipQuestionCrossing = false
) {
	let crossing: Cross[] = [];

	for (const region of group.regionsChosen) {
		for (const [index, span] of GUSData.ageSpans.entries()) {
			if (group.gender === Gender.MALE || group.gender === Gender.BOTH) {
				crossing.push({
					ageFrom: span.ageFrom,
					ageTo: span.ageTo,
					gender: Gender.MALE,
					province: region.province,
					county: region.county,
					city: region.city,
					postalCode: null,
					amount: data[region.regionId][index].male,
					answers: [],
				});
			}
			if (group.gender === Gender.FEMALE || group.gender === Gender.BOTH) {
				crossing.push({
					ageFrom: span.ageFrom,
					ageTo: span.ageTo,
					gender: Gender.FEMALE,
					province: region.province,
					county: region.county,
					city: region.city,
					postalCode: null,
					amount: data[region.regionId][index].female,
					answers: [],
				});
			}
		}
	}

	if (!skipQuestionCrossing)
		for (const question of group.questionsChosen) {
			crossing = generateQuestionCrosses(question, crossing, group);
		}

	for (const cross of crossing) {
		const id = hash(
			{
				...omit(cross, ['id', 'amount', 'feasibility']),
				answers: [],
			},
			{
				algorithm: 'md5',
			}
		);
		cross.id = id;
	}

	const amountSum = crossing.reduce((acc, curr) => acc + curr.amount, 0);

	let restArray: { id?: string; rest: number }[] = [];

	for (const cross of crossing) {
		const raw = (cross.amount * group.amountOfQuestionnaires) / amountSum;
		const floored = Math.floor(raw);
		const rest = raw - floored;

		restArray.push({ id: cross.id, rest });

		cross.amount = floored;
	}

	const resultSum = crossing.reduce((a, c) => a + c.amount, 0);

	const rest = group.amountOfQuestionnaires - resultSum;

	restArray = restArray.sort((a, b) => b.rest - a.rest).splice(0, rest);

	for (const cross of crossing) {
		if (restArray.some((r) => r.id === cross.id)) cross.amount++;
	}

	return crossing;
}

export const generateGenderCrosses = (
	crosses: Cross[],
	group: ResearchGroup
) => {
	return group.gender === Gender.BOTH
		? flatten(
				crosses.map((c) => {
					const maleCross = {
						...c,
						gender: Gender.MALE,
						amount: Math.floor(
							c.amount *
								(group.quantity.gender.male / group.amountOfQuestionnaires)
						),
					};

					const femaleCross = {
						...c,
						gender: Gender.FEMALE,
						amount: Math.floor(
							c.amount *
								(group.quantity.gender.female / group.amountOfQuestionnaires)
						),
					};

					const rest = c.amount - maleCross.amount - femaleCross.amount;

					return [maleCross, femaleCross].map((c, idx) => ({
						...c,
						amount: c.amount + (idx < rest ? 1 : 0),
					}));
				})
		  )
		: crosses.map((c) => ({ ...c, gender: group.gender }));
};

export const generateRegionCrosses = (
	crosses: Cross[],
	group: ResearchGroup
) => {
	if (
		group.regionSwitch === LocalizationTypes.COUNTRY ||
		(!group.regionsChosen.length && !group.postalCodesChosen.length)
	)
		return crosses;

	if (group.regionSwitch === LocalizationTypes.POSTAL_CODE)
		return flatten(
			crosses.map((c) => {
				const postalCodeCrosses = group.quantity.region.postalCodes.map(
					(codeCross) => {
						return {
							...c,
							province: null,
							county: null,
							city: null,
							postalCode: codeCross.code,
							amount: Math.floor(
								c.amount * (codeCross.value / group.amountOfQuestionnaires)
							),
						};
					}
				);

				const rest =
					c.amount -
					postalCodeCrosses.reduce((acc, cross) => acc + cross.amount, 0);

				for (const [idx, cross] of postalCodeCrosses.entries()) {
					cross.amount = cross.amount + (idx < rest ? 1 : 0);
				}

				return postalCodeCrosses;
			})
		);

	return flatten(
		crosses.map((c) => {
			const regionCrosses = group.quantity.region.regions.map((regionCross) => {
				return {
					...c,
					province: regionCross.region.province,
					county: regionCross.region.county,
					city: regionCross.region.city,
					postalCode: null,
					amount: Math.floor(
						c.amount * (regionCross.value / group.amountOfQuestionnaires)
					),
				};
			});

			const rest =
				c.amount - regionCrosses.reduce((acc, cross) => acc + cross.amount, 0);

			for (const [idx, cross] of regionCrosses.entries()) {
				cross.amount = cross.amount + (idx < rest ? 1 : 0);
			}

			return regionCrosses;
		})
	);
};

export const generateAgeCrosses = (crosses: Cross[], group: ResearchGroup) => {
	return !group.quantity.age.spans.length
		? crosses
		: flatten(
				crosses.map((c) => {
					const ageCrosses = group.quantity.age.spans.map((ageCross) => {
						return {
							...c,
							ageFrom: ageCross.span[0],
							ageTo: ageCross.span[1],
							amount: Math.floor(
								c.amount * (ageCross.value / group.amountOfQuestionnaires)
							),
						};
					});

					const rest =
						c.amount - ageCrosses.reduce((acc, cross) => acc + cross.amount, 0);

					for (const [idx, cross] of ageCrosses.entries()) {
						cross.amount = cross.amount + (idx < rest ? 1 : 0);
					}

					return ageCrosses;
				})
		  );
};

export const generateQuestionCrosses = (
	question: Question,
	crosses: Cross[],
	group: ResearchGroup
) => {
	return flatten(
		crosses.map((c) => {
			const questionCrosses = question.answers.map((answer) => {
				return {
					...c,
					answers: [
						...c.answers,
						pick(answer, ['id', 'translations', 'value']),
					],
					amount: Math.floor(
						c.amount * (answer.value / group.amountOfQuestionnaires)
					),
				};
			});

			const rest =
				c.amount -
				questionCrosses.reduce((acc, cross) => acc + cross.amount, 0);

			for (const [idx, cross] of questionCrosses.entries()) {
				cross.amount = cross.amount + (idx < rest ? 1 : 0);
			}

			return questionCrosses;
		})
	);
};

export const appendFeasibilityData = (
	feasibilityData: Feasibility[],
	crossing: (Cross | CrossWithoutAmount)[]
) => {
	const hashedData = feasibilityData.map((fd) => ({
		cross: omit(fd, 'feasibility'),
		feasibility: fd.feasibility,
		id: hash(
			{
				ageFrom: fd.ageFrom,
				ageTo: fd.ageTo,
				gender: fd.gender,
				answers: fd.answers.map((a) => a.id),
				province: fd.province,
				county: fd.county,
				city: fd.city,
				postalCode: fd.postalCode,
			},
			{
				algorithm: 'md5',
			}
		),
	}));

	return crossing.map((c) => ({
		...c,
		feasibility: hashedData.find((fd) => fd.id === c.id)?.feasibility || 0,
	}));
};

interface PartialSpan {
	ageFrom: number;
	ageTo: number;
}

interface CalculatedSpan extends PartialSpan {
	id: string;
	value: number;
}
export interface RepresentativeResult {
	calcGenders: {
		male: number;
		female: number;
	};
	calcSpans: CalculatedSpan[];
	calcRegions?: {
		id: string;
		value: number;
	}[];
}

export function countFeasibilityForCondition(
	cross: Cross[],
	condition: (cross: Cross) => boolean
) {
	return cross.reduce(
		(acc, cross) => (condition(cross) ? acc + (cross.feasibility ?? 0) : acc),
		0
	);
}

export function mapQuestionnaireStatusName(status: QuestionnaireStatus) {
	switch (status) {
		case QuestionnaireStatus.PENDING:
			return 'Oczekiwanie';
		case QuestionnaireStatus.COMPLETED:
			return 'Ukończono';
		case QuestionnaireStatus.QUOTAFULL:
			return 'Quotafull';
		case QuestionnaireStatus.SCREENOUT:
			return 'Screenout';
		case QuestionnaireStatus.DROPOUT:
			return 'Dropout';
		case QuestionnaireStatus.TIMEOUT:
			return 'Timeout';
		case QuestionnaireStatus.EXCLUDED:
			return 'Wykluczono';
		case QuestionnaireStatus.EXPIRED:
			return 'Ankieta wygasła';
		case QuestionnaireStatus.QUAL:
			return 'Qual';
		default:
			return 'Invalid questonnaire status!';
	}
}

export function mapQuestionnaireStatusColor(status: QuestionnaireStatus) {
	switch (status) {
		case QuestionnaireStatus.PENDING:
			return '#50a5f1';
		case QuestionnaireStatus.COMPLETED:
			return '#34c38f';
		case QuestionnaireStatus.QUOTAFULL:
			return '#556ee6';
		case QuestionnaireStatus.SCREENOUT:
			return '#f1b44c';
		case QuestionnaireStatus.DROPOUT:
			return '#f46a6a';
		case QuestionnaireStatus.TIMEOUT:
			return '#fcd9d9';
		case QuestionnaireStatus.EXCLUDED:
			return '#7f92ec';
		case QuestionnaireStatus.EXPIRED:
			return '#74788d';
		case QuestionnaireStatus.QUAL:
			return '#343a40';
		default:
			return '#ffffff';
	}
}

export function createRegionLabel(
	{ province, county, city }: Partial<Region>,
	cityOnly = false
) {
	if (city) return cityOnly ? city : `${city}, ${province}`;
	if (county) return county;
	if (province) return province;
	return 'Incorrect region name!';
}

export function compareCrosses(
	cross: Cross,
	userCross: UserCross,
	crossSwitches: CrossSwitches
) {
	let toOmit: (keyof Cross)[] = ['id', 'answers', 'amount', 'feasibility'];
	if (!crossSwitches.gender) toOmit.push('gender');
	if (!crossSwitches.region) {
		toOmit = [...toOmit, 'province', 'county', 'city', 'postalCode'];
	}
	if (!crossSwitches.age) {
		toOmit = [...toOmit, 'ageFrom', 'ageTo'];
	}
	const isPartialEqual = isEqual(omit(cross, toOmit), omit(userCross, toOmit));
	if (!isPartialEqual) return false;
	if (!userCross.answers.length) return true;
	return userCross.answers.every((answer) =>
		cross.answers.some((ans) => ans.id === answer.id)
	);
}

export function getProvinceRegions(): Region[] {
	const provinces = [
		'Dolnośląskie',
		'Kujawsko-Pomorskie',
		'Lubelskie',
		'Lubuskie',
		'Łódzkie',
		'Małopolskie',
		'Mazowieckie',
		'Opolskie',
		'Podkarpackie',
		'Podlaskie',
		'Pomorskie',
		'Świętokrzyskie',
		'Wielkopolskie',
		'Zachodniopomorskie',
		'Województwo Śląskie',
		'Warmińsko-Mazurskie',
	];
	return provinces
		.map((p) => ({ province: p, county: null, city: null }))
		.map((p) => ({ ...p, regionId: hash(p, { algorithm: 'md5' }) }));
}

export function calculateRegionQuantity(
	amount: number,
	quantityRegion: ResearchGroup['quantity']['region']
): ResearchGroup['quantity']['region'] {
	return {
		...quantityRegion,
		regions: quantityRegion.regions.map((region, index, array) => ({
			...region,
			value:
				Math.floor(amount / array.length) +
				(index < amount % array.length ? 1 : 0),
		})),
		postalCodes: quantityRegion.postalCodes.map((code, index, array) => ({
			...code,
			value:
				Math.floor(amount / array.length) +
				(index < amount % array.length ? 1 : 0),
		})),
	};
}

export function checkIfRegionsAreOfSameType(regions: Region[]) {
	const mappedRegionsTypes = regions.map((reg) => {
		if (reg.city) return 'city';
		if (reg.county) return 'county';
		return 'province';
	});

	return mappedRegionsTypes.every((type) => type === mappedRegionsTypes[0]);
}

export function getAveragedRepresentativeData(
	n: number,
	spans: PartialSpan[],
	regions: Region[],
	gender = Gender.BOTH
): RepresentativeResult {
	const ageFrom = spans[0].ageFrom;
	const ageTo = spans[spans.length - 1].ageTo;

	let calcSpans = [],
		i = ageFrom;

	while (i < ageTo) {
		const t = i;
		const nextSpan = spans.find(
			(sp) => sp.ageFrom <= t && sp.ageTo >= t
		) as PartialSpan;
		const from = nextSpan.ageFrom < t ? t : nextSpan.ageFrom;
		const to = nextSpan.ageTo > ageTo ? ageTo : nextSpan.ageTo;
		calcSpans.push({ ageFrom: from, ageTo: to });
		i = to + 1;
	}

	const mappedStats = GUSData.ageGenderStats.map((s) =>
		gender === Gender.BOTH
			? s
			: {
					...s,
					all:
						gender === Gender.FEMALE
							? s.female
							: gender === Gender.MALE
							? s.male
							: s.all,
					male: gender === Gender.MALE ? s.male : 0,
					female: gender === Gender.FEMALE ? s.female : 0,
			  }
	);

	const totals = mappedStats
		.filter((s) => s.age >= ageFrom && s.age <= ageTo)
		.reduce(
			(acc, s) => {
				return {
					all: acc.all + s.all,
					male: acc.male + s.male,
					female: acc.female + s.female,
				};
			},
			{
				all: 0,
				male: 0,
				female: 0,
			}
		);

	calcSpans = calcSpans
		.map((cs) => {
			return mappedStats
				.filter((s) => s.age >= cs.ageFrom && s.age <= cs.ageTo)
				.reduce(
					(acc, s) => {
						return {
							...acc,
							value: acc.value + s.all,
						};
					},
					{
						ageFrom: cs.ageFrom,
						ageTo: cs.ageTo,
						value: 0,
					}
				);
		})
		.map((cs) => {
			return {
				...cs,
				value: cs.value / totals.all,
			};
		})
		.map((cs) => {
			return {
				...cs,
				value: Math.floor(n * cs.value),
			};
		});

	const spansTotal = calcSpans.reduce((acc, cs) => acc + cs.value, 0);

	calcSpans = calcSpans.map((cs, idx) => {
		const value = cs.value + (idx < n % spansTotal ? 1 : 0);

		return {
			...cs,
			id: crypto.randomUUID(),
			value,
		};
	});

	const nMale = Math.floor((totals.male * n) / totals.all);
	const nFemale = Math.floor((totals.female * n) / totals.all);

	const calcGenders = {
		male: nMale + (n - nMale - nFemale),
		female: nFemale,
	};

	if (regions.length > 1) {
		let includeRegions = checkIfRegionsAreOfSameType(regions);

		if (!!regions[0].city)
			includeRegions = regions.every((reg) => {
				return (
					reg.province &&
					reg.city &&
					GUSData.regionStats[reg.province].city[reg.city]
				);
			});

		if (!includeRegions)
			return {
				calcGenders,
				calcSpans,
			};

		const regionsWithValues = regions.map((reg) => {
			if (reg.city)
				return {
					id: reg.regionId,
					value:
						GUSData.regionStats[reg.province as string].city[
							reg.city as string
						],
				};
			if (reg.county) {
				return {
					id: reg.regionId,
					value:
						GUSData.regionStats[reg.province as string].county[
							reg.county.split(' ').slice(1).join(' ')
						],
				};
			}
			return {
				id: reg.regionId,
				value: GUSData.regionStats[reg.province as string].amount,
			};
		});

		const total = regionsWithValues.reduce((acc, reg) => acc + reg.value, 0);

		const calcRegions = regionsWithValues.map((reg) => {
			const value = Math.floor(n * (reg.value / total));
			return { id: reg.id, value };
		});

		let rest = n - calcRegions.reduce((acc, reg) => acc + reg.value, 0);

		for (const reg of calcRegions) {
			if (rest) {
				reg.value++;
				rest--;
			}
		}

		return {
			calcGenders,
			calcSpans,
			calcRegions,
		};
	}

	return {
		calcGenders,
		calcSpans,
	};
}

export function getRepresentativeDataFromCrossing(
	group: ResearchGroup,
	data: GusRegionStats
): RepresentativeResult {
	const crossing = generateCrossingFromData(group, data, true);

	const calcGenders = {
		male: 0,
		female: 0,
	};

	const calcSpans = GUSData.ageSpans.map((span) => ({
		...span,
		id: crypto.randomUUID(),
		value: 0,
	}));

	const calcRegionsSignature: StringSignature<number> = {};

	for (const regionId in data) {
		calcRegionsSignature[regionId] = 0;
	}

	for (const cross of crossing) {
		cross.gender === Gender.MALE
			? (calcGenders.male += cross.amount)
			: (calcGenders.female += cross.amount);

		const regionId = hash(pick(cross, ['province', 'county', 'city']), {
			algorithm: 'md5',
		});
		calcRegionsSignature[regionId] += cross.amount;

		const span = calcSpans.find((sp) => sp.ageFrom === cross.ageFrom);
		if (!span) continue;

		span.value += cross.amount;
	}

	const calcRegions = Object.entries(calcRegionsSignature).map(
		([id, value]) => ({
			id,
			value,
		})
	);

	return { calcGenders, calcSpans, calcRegions };
}

export function countCrossSwitches(switches: CrossSwitches) {
	return Object.values(switches).reduce((acc, s) => acc + Number(s), 0);
}

export function countCrossing(group: ResearchGroup) {
	const gender = group.gender === Gender.BOTH ? 2 : 1;
	const ageSpans = group.quantity.age.spans.length || 1;
	const regions = group.regionsChosen.length || 1;
	const postalCodes = group.postalCodesChosen.length || 1;

	let sum = gender * ageSpans * regions * postalCodes;

	for (const question of group.questionsChosen) {
		sum = sum * question.answers.length;
	}

	return sum;
}

export function getChosenQuestions(profiles: ProfileChosen[]) {
	return profiles.reduce<Question[]>((acc, current) => {
		for (const question of current.questions) {
			if (!!question.answers.length) acc.push(question);
		}
		return acc;
	}, []);
}

export function getAgeLabel(from: number, to: number) {
	return from !== to ? `${from} lat - ${to} lat` : `${from} lat`;
}

export function generateStatisticsGenderCrosses(
	crosses: Cross[],
	genderType: Gender
) {
	return genderType === Gender.BOTH
		? flatten(
				crosses.map((c) => {
					const maleCross = {
						...c,
						gender: 0,
					};

					const femaleCross = {
						...c,
						gender: 1,
					};

					return [maleCross, femaleCross];
				})
		  )
		: crosses.map((c) => ({ ...c, gender: genderType }));
}

export function generateStatisticsRegionCrosses(
	crosses: Cross[],
	regions: Region[],
	postalCodes: string[],
	type: LocalizationTypes
) {
	if (!regions.length && !postalCodes.length) return crosses;

	if (type === LocalizationTypes.POSTAL_CODE)
		return flatten(
			crosses.map((c) => {
				const postalCodeCrosses = postalCodes.map((code) => {
					return {
						...c,
						province: null,
						county: null,
						city: null,
						postalCode: code,
					};
				});

				return postalCodeCrosses;
			})
		);

	return flatten(
		crosses.map((c) => {
			const regionCrosses = regions.map((region) => {
				return {
					...c,
					province: region.province || null,
					county: region.county || null,
					city: region.city || null,
					postalCode: null,
				};
			});

			return regionCrosses;
		})
	);
}

export function generateStatisticsAgeCrosses(
	crosses: Cross[],
	spans: AgeSpanWithoutValue[]
) {
	return !spans.length
		? crosses
		: flatten(
				crosses.map((c) => {
					const ageCrosses = spans.map((ageCross) => {
						return {
							...c,
							ageFrom: ageCross.span[0],
							ageTo: ageCross.span[1],
						};
					});

					return ageCrosses;
				})
		  );
}

export function generateStatisticsQuestionCrosses(
	crosses: Cross[],
	question: Question
) {
	return flatten(
		crosses.map((c) => {
			const result = question.answers.map((answer) => {
				return {
					...c,
					answers: [
						...c.answers,
						pick(answer, ['id', 'translations', 'value']),
					],
				};
			});

			return result;
		})
	);
}

export function getEarliestResearchGroupStartsDate(
	researchGroups: ResearchGroup[]
) {
	return researchGroups.reduce((acc, current, idx) => {
		if (!idx) return new Date(current.startsAt);

		return new Date(current.startsAt) < acc ? new Date(current.startsAt) : acc;
	}, new Date());
}

export function formatResearchHistoryDate(date: moment.MomentInput) {
	return moment(date).format('D MMM HH:mm');
}

export function validateResearchUrl(url?: string) {
	if (!url) return false;

	const urlReg =
		/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
	const idReg = /\[ID\]/;
	const urlRegex = new RegExp(urlReg);
	const idRegex = new RegExp(idReg);
	return urlRegex.test(url) && idRegex.test(url);
}
